/**
 * Reading and writing a date and time in English, whatever the browser thinks.
 *
 * `<input type="datetime-local">` draws its own calendar, month names and
 * format hint in the **browser's** UI language, and Chrome and Safari ignore
 * the document's `lang`. ABG's browser is set to `nb`, so a captain app that is
 * English everywhere else was handing them a Norwegian picker with no
 * page-level override available. `captain-pwa`'s `DateTimeField` replaces the
 * control on desktop; this is the part of it worth testing.
 *
 * The value contract is `datetime-local`'s own -- `YYYY-MM-DDTHH:mm`, in the
 * device's zone -- so it interoperates with `toLocalInputValue` and
 * `fromLocalInputValue` in `./time` and nothing downstream knows which control
 * a captain saw.
 *
 * Only the captain app imports this. The back office is Norwegian by design
 * (decision 17) and its native picker is already in the right language.
 */

/** English, explicit. `toLocaleString` would read the browser locale, which is
 *  the entire problem this component exists to solve. */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]
export const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
/** Monday first: Norway, and every chart-table and tide-book a captain owns. */
export const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export const pad = (n: number) => String(n).padStart(2, '0')

/** `2026-09-03T14:30` -> `03 Sep 2026 14:30`. Empty in, empty out. */
export function formatDisplay(value: string): string {
  const parts = splitValue(value)
  if (!parts) return ''
  const { year, month, day, hour, minute } = parts
  return `${pad(day)} ${MONTHS[month - 1]} ${year} ${pad(hour)}:${pad(minute)}`
}

export function splitValue(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const [, y, mo, d, h, mi] = match
  return {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
  }
}

export function toValue(year: number, month: number, day: number, hour: number, minute: number) {
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
}

/** True when this really is that calendar day -- `new Date(2026, 1, 31)` is
 *  3 March, and a picker that silently accepted "31 Feb" would file a window
 *  the captain did not choose. */
export function isRealDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day)
  return (
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  )
}

export const ACCEPTED_FORMATS = '03 Sep 2026 14:30 · 2026-09-03 14:30 · 03.09.2026 14:30'

/**
 * Parse what a captain typed. Returns the canonical value, or null.
 *
 * Accepts, case-insensitively and forgiving about spacing:
 *   `3 Sep 2026 14:30`, `3 September 2026 14:30`  -- what we display
 *   `2026-09-03 14:30`, `2026-09-03T14:30`        -- ISO
 *   `03.09.2026 14:30`                            -- Norwegian numeric, day first
 *
 * Refuses `03/09/2026`, which means two different days either side of the
 * Atlantic. Guessing is not available to something that files with an
 * authority.
 */
export function parseTyped(text: string): string | null {
  const clean = text.trim().replace(/\s+/g, ' ')
  if (!clean) return null

  const time = /(\d{1,2})[:.](\d{2})\s*$/.exec(clean)
  if (!time) return null
  const hour = Number(time[1])
  const minute = Number(time[2])
  // Explicit integer checks rather than range checks alone: `Number(undefined)`
  // is NaN, and every comparison against NaN is false, so a range check on its
  // own would wave a malformed time straight through.
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null
  if (hour > 23 || minute > 59) return null
  const datePart = clean.slice(0, time.index).trim().replace(/[T,]+$/, '').trim()

  let year: number | undefined
  let month: number | undefined
  let day: number | undefined

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(datePart)
  const dotted = /^(\d{1,2})[.](\d{1,2})[.](\d{4})$/.exec(datePart)
  const named = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/.exec(datePart)

  if (iso) {
    year = Number(iso[1])
    month = Number(iso[2])
    day = Number(iso[3])
  } else if (dotted) {
    day = Number(dotted[1])
    month = Number(dotted[2])
    year = Number(dotted[3])
  } else if (named) {
    day = Number(named[1])
    year = Number(named[3])
    const word = (named[2] ?? '').toLowerCase()
    const index = MONTHS_FULL.findIndex(
      (full) => full.toLowerCase() === word || full.slice(0, 3).toLowerCase() === word.slice(0, 3),
    )
    if (index < 0) return null
    month = index + 1
  } else {
    return null
  }

  if (!year || !month || !day || month > 12 || !isRealDate(year, month, day)) return null
  return toValue(year, month, day, hour, minute)
}
